/**
 * Access control.
 *
 * One `as const` matrix drives the sidebar, the role editor and every
 * server-side guard, so a permission string that does not exist is a COMPILE
 * error rather than a check that silently never passes. The design states the
 * model exactly: "Roles are additive permission sets; a user has exactly one
 * role plus optional per-brand scope."
 */

/**
 * `writes` says whether exercising the permission CHANGES anything.
 *
 * It exists so "a view permission must not gate a write" is a test rather than
 * a habit — which is how a support reply, visible to the customer, ended up
 * behind `players.view` and reachable by a read-only Auditor.
 */
export const PERMISSIONS = [
  { key: 'players.view', label: 'View players', domain: 'players', requiresTwoFactor: false, writes: false },
  { key: 'players.restrict', label: 'Restrict / suspend player', domain: 'players', requiresTwoFactor: false, writes: true },
  { key: 'kyc.review', label: 'Approve KYC', domain: 'kyc', requiresTwoFactor: false, writes: true },
  { key: 'funds.approve', label: 'Approve withdrawal (checker)', domain: 'funds', requiresTwoFactor: true, writes: true },
  { key: 'funds.adjust', label: 'Submit balance adjustment', domain: 'funds', requiresTwoFactor: true, writes: true },
  { key: 'promo.publish', label: 'Publish promotion', domain: 'promo', requiresTwoFactor: true, writes: true },
  { key: 'access.manage', label: 'Manage roles & users', domain: 'access', requiresTwoFactor: true, writes: true },
  { key: 'audit.export', label: 'Export audit logs', domain: 'audit', requiresTwoFactor: false, writes: false },

  /*
   * The design's grid draws eight capabilities, and for a while every module
   * built after it was gated on the nearest one that already existed. That put
   * twenty-three endpoints behind `players.view`, which had three consequences
   * worth naming rather than living with:
   *
   *   - the full financial ledger and every agent and affiliate commission plan
   *     were readable by any role that could open a player record;
   *   - `/api/payments/*` went the other way, gating a READ on `funds.adjust`,
   *     a two-factor WRITE permission, so a Risk Officer could not see a
   *     payment rail at all;
   *   - and posting a reply to a customer's support ticket — a write, visible
   *     to that customer — was gated on a VIEW permission, so the Auditor role,
   *     which is read-only by design, could write into the thread.
   *
   * A view permission must never gate a write, and a write permission must
   * never gate a view. These three close that.
   */
  { key: 'transactions.view', label: 'View the financial ledger', domain: 'funds', requiresTwoFactor: false, writes: false },
  { key: 'partners.view', label: 'View agents & affiliates', domain: 'partners', requiresTwoFactor: false, writes: false },
  { key: 'support.respond', label: 'Reply to a support ticket', domain: 'support', requiresTwoFactor: false, writes: true },
  /*
   * A redemption verdict spends points that carry a cash value, so it is a
   * money-adjacent decision and gets its own key rather than borrowing
   * players.view — which is how the support reply ended up reachable by a
   * read-only role. Deliberately NOT funds.approve: approving a ₱100 GCash
   * reward is not the same authority as releasing a payout, and folding them
   * together would widen the payout permission to reach the Points Mall.
   */
  { key: 'points.verdict', label: 'Approve or refuse a redemption', domain: 'points', requiresTwoFactor: false, writes: true },
  /*
   * Running a report is a READ, and `audit.export` already covers it — an
   * Auditor whose job is to pull the regulatory extract must be able to pull
   * it. Scheduling one is not: it decides what fires unattended at 06:00 and
   * to which brand's scope, forever, with nobody watching. That is a write, so
   * it is a separate capability and a read-only role does not hold it.
   */
  /*
   * Onboarding and governing a PARTNER — filing an agent or affiliate,
   * approving one, moving it onto a commission plan, recording a KYB outcome,
   * closing a quality review.
   *
   * These were gated on `funds.approve` for want of anything better, and that
   * key means "release a player's withdrawal". Approving a partner is not the
   * same authority: it starts paying commission on a relationship somebody set
   * up, which is why it carries its own maker-checker. Folding the two together
   * meant a role could be given the power to onboard partners only by also
   * being given the power to move player money.
   *
   * Two-factor, like every other capability that moves money — commission is
   * money, and the partner it is paid to is chosen here.
   *
   * It is held by exactly the roles that held `funds.approve`, so this change
   * renames the authority without widening it. Whether a Risk Officer should
   * hold it — they can suspend a player but cannot close a partner's quality
   * review — is a product question, and deliberately not answered here.
   */
  { key: 'partners.manage', label: 'Onboard & govern partners', domain: 'partners', requiresTwoFactor: true, writes: true },
  { key: 'reports.schedule', label: 'Schedule a report to run automatically', domain: 'reports', requiresTwoFactor: false, writes: true },

  /*
   * Writing an internal note on a player.
   *
   * `player.player_note` is append-only by trigger and is the evidence trail
   * every restriction dialog points the next operator at — "read the note
   * before deciding whether to lift it". It could not be gated on
   * `players.view`, because a view permission must never gate a write; that is
   * the exact hole the support-reply key was added to close. And it could not
   * be folded into `players.restrict` either: the people who write the most
   * notes are Support Agents, who must not also gain the power to bar an
   * account in order to record what a caller told them.
   *
   * No second factor. A note moves no money and bars nobody; requiring MFA to
   * write one would make the evidence trail the hardest thing on the screen to
   * add to, which is how a trail stays empty.
   */
  { key: 'players.note', label: 'Write an internal note on a player', domain: 'players', requiresTwoFactor: false, writes: true },

  /*
   * Moving a player's TIER.
   *
   * A commercial decision, not a control: a tier drives the VIP programme and
   * the commission a partner earns on that player, so it belongs with the roles
   * that answer for revenue rather than with the ones that answer for risk. A
   * Risk Officer can bar an account and cannot promote one to VIP 4, which is
   * the separation this key exists to state.
   */
  { key: 'players.tier', label: 'Change a player’s tier', domain: 'players', requiresTwoFactor: false, writes: true },
  /*
   * MARKING AN ACCOUNT AS A TEST ACCOUNT — PAGCOR item 33(c), sql/1003.
   *
   * ITS OWN KEY, not folded into `players.restrict`. Restricting somebody limits
   * what THEY may do; this changes what the OPERATOR reports as revenue. A test
   * account's play, deposits and withdrawals leave every figure an inspector
   * reads — including the months before it was marked, which are retracted.
   *
   * TWO-FACTOR, like the other keys that move money or authority. The question an
   * inspection asks about an excluded account is "who decided that", and the
   * answer should be somebody who proved they were themselves.
   */
  { key: 'players.test_account', label: 'Mark a player as a test account', domain: 'players', requiresTwoFactor: true, writes: true },

  /*
   * READING the platform's configuration and its health.
   *
   * The third time this codebase has had to separate a read from the write it
   * was filed next to, and the same mistake each time: `GET
   * /api/platform/settings`, `/settings/history`, `/brands` and `/health` were
   * all gated on `access.manage` — "manage roles & users", a two-factor WRITE.
   * That had two consequences.
   *
   * The first is the rule itself: a read must never be gated on a write
   * capability, because the only way to grant the read is to grant the power to
   * change. Asking "what is the withdrawal payout-ratio hold set to?" required
   * being handed the ability to rewrite the permission matrix.
   *
   * The second is who was locked out. Only Super Admin holds `access.manage`,
   * so the Finance Lead could not read the payout thresholds that decide their
   * queue, the Risk Officer could not read the KYC auto-pass confidence behind
   * their alerts, and the Auditor — a role whose entire job is to read the
   * configuration and the log of who changed it — could read neither. The
   * console's own configuration log was unreadable by the only role that exists
   * to read logs.
   *
   * It also made the audit-chain verdict unreachable. `GET /api/platform/audit`
   * answers `audit.export` holders, but it is rendered on /system, which was
   * walled behind `access.manage` — so of the four roles then holding
   * audit.export, three could never see the walk. This key is held by exactly
   * the audit.export holders — those four, and since item 22 the PAGCOR
   * Regulator — so the screen and the panel on it agree about who may look.
   *
   * NOT a partner-style allow-list view. `platform.affiliate_policy` narrows
   * five keys for `dayon_partner`, an OUTSIDE tenant that has no business
   * learning the KYC threshold. The four house roles are accountable for the
   * parameters they are being shown, and the PAGCOR Regulator is the inspector
   * they answer to; the answer for both is a read capability, not a curated
   * subset that would quietly hide the one parameter an auditor came to check.
   *
   * No second factor: nothing here changes. Every write on these screens keeps
   * `access.manage` and keeps its second factor.
   */
  { key: 'platform.view', label: 'View platform configuration', domain: 'platform', requiresTwoFactor: false, writes: false },

  /*
   * CONFIGURING THE PLATFORM ITSELF — creating and editing a brand, moving a
   * parameter, composing an announcement.
   *
   * The write half of `platform.view`, and a separate key from `access.manage`
   * for a reason the other direction of this same mistake already taught this
   * codebase: `access.manage` means "manage roles & users". Filing brand
   * creation under it would mean the only way to let somebody renew a gaming
   * licence is to hand them the power to rewrite the permission matrix — the
   * mirror image of the read-gated-on-a-write problem `platform.view` exists to
   * fix, and just as wrong.
   *
   * WHAT IS BEHIND IT is a fair summary of why it is two-factor and why the
   * matrix gives it to Super Admin alone:
   *
   *   - `platform.brand.domain` is the TENANCY BOUNDARY. The player portal
   *     resolves which brand a new registration belongs to by matching the
   *     request's Host header against that column, so whoever may edit it may
   *     decide whose players, whose licence and whose commission a host
   *     produces. It is the single most consequential string in the database.
   *   - `licence_expires_at` is what the dashboard's 60-day warning counts down
   *     to. Moving it is asserting to every operator on the platform that a
   *     regulator has renewed something.
   *   - `platform.setting` holds the withdrawal payout-ratio hold, the KYC
   *     auto-pass confidence and the affiliate quality-review threshold. Each
   *     one silently changes an automated decision about somebody's money.
   *
   * Two-factor, like every other capability that moves money or decides who
   * may. Held by Super Admin only: `platform.view` deliberately widened the
   * READ — to the four roles accountable for these parameters, and to the
   * PAGCOR Regulator who inspects them — and this key deliberately does not
   * widen the WRITE to match. A Finance Lead reads the payout hold that governs
   * their queue; changing it is a different act.
   */
  { key: 'platform.manage', label: 'Configure brands, parameters & announcements', domain: 'platform', requiresTwoFactor: true, writes: true },

  /*
   * GIVING A PLAYER A BONUS, and taking one back.
   *
   * The fourth time this file has had to separate two authorities that were
   * filed together, and the clearest case yet. `promo.publish` means "decide
   * what a campaign OFFERS": it drafts terms, puts them in front of players and
   * retires the version that came before. This means "give THIS player THAT
   * offer, now, out of the budget" — a credit to somebody's wallet, funded from
   * the operator's own PROMO_FUNDING account, posted through the double-entry
   * ledger like any other movement of money.
   *
   * Folding the two together would mean the only way to let a VIP host make
   * good on a complaint is to hand them the power to retire every live campaign
   * on the platform. It would also make the reverse true, which is worse: the
   * person who writes the terms would be the same person who decides who gets
   * paid under them, with no separation between the two acts at all.
   *
   * Two-factor, like every other capability here that moves money.
   *
   * Held by Super Admin only, which is exactly who holds `promo.publish` today
   * — so this SPLITS an authority rather than widening one. Whether a Finance
   * Lead should also hold it is a product question, and answering it by handing
   * the key out here is how a permission matrix grows grants nobody decided on.
   */
  { key: 'promo.grant', label: 'Grant & forfeit a player bonus', domain: 'promo', requiresTwoFactor: true, writes: true },

  /*
   * THE GAME CATALOGUE'S OWN WRITE CAPABILITY.
   *
   * Game management had no key of its own. `POST /api/games/status` — the one
   * write the screen has ever had — gates on `access.manage`, with a comment in
   * `services/api/src/modules/backoffice/catalogue.ts` saying so out loud and
   * calling it deliberately tighter than the design intends. `access.manage`
   * means "manage roles & users"; only Super Admin holds it. The practical
   * effect was that taking a title into maintenance required the authority to
   * rewrite the permission matrix, and every further write this screen needs
   * would have inherited the same gate — which would have made the whole module
   * Super-Admin-only permanently rather than by accident.
   *
   * TWO-FACTOR, per the human's ruling on this batch: "game managers can work;
   * on/off changes still require 2FA." Taking a provider live is what admits a
   * real integration to real players' money, and taking one down is the only
   * kill switch that exists for one — both belong with the capabilities that
   * carry a second factor, not below them.
   *
   * Held by Super Admin at launch, which is exactly who reaches this screen's
   * writes today, so this SPLITS an authority rather than widening one: nobody
   * gains a capability they did not already have through `access.manage`. WHICH
   * OTHER ROLE SHOULD HOLD IT IS AN OPEN PRODUCT QUESTION (`OQ-GM-01`): none of
   * the roles in the matrix is a game manager, and handing the key out here is how a
   * matrix grows grants nobody decided on. It is a one-line grant, in this
   * array and in `sql/600_games_manage.sql`, once that is answered.
   */
  { key: 'games.manage', label: 'Manage the game catalogue', domain: 'games', requiresTwoFactor: true, writes: true },
  /**
   * `points.manage` — the POINTS catalogue's own write key, and the same
   * argument `games.manage` makes one entry above.
   *
   * Nothing could create a reward: the console drew "Add reward" hard-disabled
   * and said the catalogue was read-only since the seed. The only keys that
   * could plausibly have carried it were both wrong in a way worth naming.
   *
   * NOT `points.verdict`. That is "approve or refuse a redemption" — deciding
   * one player's request. Defining what the catalogue OFFERS, and what cash face
   * value it carries, is a different act, and folding them together would let
   * whoever approves a redemption also invent the reward it was for. The
   * separation between setting the terms and deciding a case is the one this
   * platform keeps everywhere else.
   *
   * NOT `platform.manage` either, for the reason the key above gives: it is held
   * by Super Admin alone, so the whole points catalogue would have been
   * Super-Admin-only permanently rather than by accident.
   *
   * TWO-FACTOR. A reward carries a cash face value that a player redeems points
   * for, so adding one is a commitment of money — the same standing as taking a
   * provider live, not below it.
   *
   * Super Admin at launch, which is who reaches this today, so this SPLITS an
   * authority rather than widening one.
   *
   * ── WHO SHOULD HOLD IT NEXT IS NOT FINANCE LEAD ──────────────────────────
   *
   * An earlier draft of this note said Finance Lead was the obvious second
   * holder, because they already hold `points.verdict` and `players.tier` — the
   * platform's two other commercial keys. That reasoning was wrong, and it was
   * wrong in the exact way this key was created to avoid.
   *
   * FINANCE LEAD HOLDS `points.verdict`. An admin holding both keys can define a
   * reward's cash value AND approve the redemptions made against it. The
   * maker-checker constraint on `points.redemption` does not reach this:
   * `redemption_maker_is_not_checker` only refuses an admin approving a request
   * THEY raised, and a player-raised redemption has no `initiated_by` at all. So
   * the path is open — price a reward absurdly, have somebody redeem it, approve
   * it — and separating the two keys on paper while granting them to one role
   * would rebuild the conflation through the back door.
   *
   * NO ROLE IN THE CURRENT MATRIX IS RIGHT. That is a product gap and is worth
   * recording as one rather than closing with a grant. Two honest ways out:
   *   · a commercial role that holds `points.manage` and NOT `points.verdict`,
   *     which means a sixth role and a decision about who they are; or
   *   · maker-checker on the CATALOGUE itself — a second admin confirms a new
   *     reward before it is offered — which makes the pair safe to hold together
   *     and is the smaller change of the two.
   *
   * Super Admin holding both is a pre-existing property of that role rather than
   * something this key introduced: it holds every key on the platform, and that
   * is the accepted baseline here.
   */
  { key: 'points.manage', label: 'Manage the points catalogue', domain: 'points', requiresTwoFactor: true, writes: true },
  /**
   * `points.adjust` — PROPOSING a points movement, which is not signing one.
   *
   * Mirrors `funds.adjust` beside `funds.approve` on the money side, and for
   * the reason that split exists: whoever notices a player is owed points is
   * not who should move them alone. Folding this into `points.verdict` would
   * mean only approvers can raise a request, which makes the queue useless to
   * the people who see the problem.
   *
   * NO SECOND FACTOR, unlike `points.manage`. A proposal moves nothing — it
   * waits for a second signature, and that signature is where the factor
   * belongs. Making the low-trust half harder than it needs to be is how a
   * queue stops being used.
   *
   * Super Admin at launch. SUPPORT AGENT is the candidate worth considering and
   * is deliberately not granted here: unlike the catalogue key, a maker key is
   * safe to spread because a proposal moves nothing and Support Agent holds no
   * points key at all, so they cannot sign their own. sql/780 says the same.
   */
  { key: 'points.adjust', label: 'Propose a points adjustment', domain: 'points', requiresTwoFactor: false, writes: true },
] as const;

export type Permission = (typeof PERMISSIONS)[number]['key'];

export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key) as readonly Permission[];

/**
 * Permissions whose holder MUST have a second factor.
 * "2FA is mandatory for any role holding funds.approve or promo.publish."
 */
export const TWO_FACTOR_PERMISSIONS = PERMISSIONS.filter((p) => p.requiresTwoFactor).map(
  (p) => p.key,
) as readonly Permission[];

/** Permissions that change state. A read-only role must hold none of them. */
export const WRITE_PERMISSIONS = PERMISSIONS.filter((p) => p.writes).map(
  (p) => p.key,
) as readonly Permission[];

/**
 * Roles that may only read. Asserted against WRITE_PERMISSIONS in the tests, and
 * matched by NAME by the Access screen's refusal to grant one a write key.
 */
export const READ_ONLY_ROLES = ['Auditor', 'PAGCOR Regulator'] as const;

/**
 * Roles whose holders always see EVERY brand, matched by name like the list
 * above. An invite or a brand-scope change that names brands is refused for one,
 * and so is a role change into one while the person still has brand limits —
 * see services/api/src/modules/access/routes.ts.
 */
export const EVERY_BRAND_ROLES = ['PAGCOR Regulator'] as const;

/**
 * The six roles. The first eight columns are the design's grid verbatim; the
 * rest are the capabilities above, assigned by what the role is FOR.
 *
 * Support Agent gets `support.respond` and neither of the two new view
 * permissions: answering a player is the job, reading the whole ledger and
 * every partner's commission is not. Auditor is the mirror image — it reads
 * both and may not write into a customer-facing thread, which is the specific
 * hole this closes.
 */
export const ROLE_MATRIX = {
  'Super Admin': [
    'players.view', 'players.restrict', 'kyc.review', 'funds.approve',
    'funds.adjust', 'promo.publish', 'access.manage', 'audit.export',
    'transactions.view', 'partners.view', 'support.respond', 'points.verdict',
    'reports.schedule', 'partners.manage', 'players.note', 'players.tier',
    'platform.view',
    /* Excluding an account from reported revenue. Super Admin alone at launch:
       it is the operator's own revenue figure, and nobody should gain authority
       over it as a side effect of a role they already hold. Finance Lead is the
       obvious second holder and is deliberately not assumed. */
    'players.test_account',
    /* And the write half of it. Super Admin alone: this key edits the host a
       brand's players are resolved from and the licence expiry the dashboard
       counts down to. */
    'platform.manage',
    /* Granting a player a bonus. Beside promo.publish rather than folded into
       it — see the key's own note. sql/270 writes the same grant on a database
       that has already been migrated; this is what a fresh seed writes. */
    'promo.grant',
    /* The game catalogue's own write key, in place of this screen borrowing
       access.manage. Super Admin alone at launch, so nobody gains authority
       they did not already hold — see the key's own note, and OQ-GM-01 for the
       role assignment that is still open. sql/600 writes the same grant on a
       database that has already been migrated. */
    'games.manage',
    /* The points catalogue's own write key, on the same reasoning and with the
       same open question: Finance Lead is the obvious second holder and nobody
       has decided it. sql/740 writes the same grant on a migrated database. */
    'points.manage',
    /* Proposing a points adjustment. The maker half of a pair whose checker is
       points.verdict — see the key's own note for why it is separate and why it
       carries no second factor. sql/780 writes the same grant. */
    'points.adjust',
  ],
  'Finance Lead': [
    'players.view', 'funds.approve', 'funds.adjust', 'audit.export',
    'transactions.view', 'partners.view', 'points.verdict', 'reports.schedule',
    'partners.manage',
    /* Tier is the commercial dimension of a player, and this is the commercial
       role. The note comes with it: a Finance Lead who moves a tier has to be
       able to record why on the account as well as in the audit trail. */
    'players.note', 'players.tier',
    /* The payout thresholds and hold ratios that decide this role's queue live
       in platform.setting, and reading one used to require access.manage — the
       power to rewrite the permission matrix. */
    'platform.view',
  ],
  'Risk Officer': [
    'players.view', 'players.restrict', 'kyc.review', 'audit.export',
    'transactions.view', 'partners.view',
    /* Notes but NOT tier. A risk decision is exactly the kind that the next
       operator has to read before lifting; promoting a player is not risk's. */
    'players.note',
    /* The KYC auto-pass confidence and the risk parameters this role's alerts
       are raised against are platform settings. Reading them is the job. */
    'platform.view',
  ],
  /* A Support Agent takes the call and writes down what was said. That is the
     single most common reason a note exists, and it is why notes are their own
     capability rather than part of players.restrict — recording a conversation
     must not require the power to bar the account it was about. */
  'Support Agent': ['players.view', 'kyc.review', 'support.respond', 'players.note'],
  /* The read-only role reads the CONFIGURATION and the log of who changed it —
     that is what an auditor is for, and platform.view writes nothing. */
  Auditor: [
    'players.view', 'audit.export', 'transactions.view', 'partners.view',
    'platform.view',
  ],
  /* PAGCOR's own reader (item 22, ruling R6): every report on every brand, the
     audit trail and the sign-in trail with their export, and the platform's
     configuration and checks. Read-only, and always every brand. NOT the
     Auditor's players.view, transactions.view or partners.view — the three keys
     whose screens show players' names, emails, phones and payout-account holders.
     The audit trail is read whole, because its chain can only be re-checked on
     whole entries, so it carries what was recorded there: the email a player
     signed up with, the name a player chose, IP addresses, staff names and what
     staff typed. sql/1021 creates the role and these two grants. */
  'PAGCOR Regulator': ['audit.export', 'platform.view'],
} as const satisfies Record<string, readonly Permission[]>;

export type RoleName = keyof typeof ROLE_MATRIX;
export const ROLE_NAMES = Object.keys(ROLE_MATRIX) as RoleName[];

export interface Principal {
  id: string;
  role: string;
  isSuper: boolean;
  permissions: readonly string[];
  /** Empty means EVERY brand. */
  brandIds: readonly string[];
  /** Whether the second factor was actually satisfied in this session. */
  mfa: boolean;
}

export const can = (principal: Principal, permission: Permission): boolean =>
  principal.permissions.includes(permission);

/**
 * Whether this principal is required to hold a second factor at all.
 * Computed from what they can DO, never stored as a flag that can drift from
 * the role's actual grants.
 */
export const requiresTwoFactor = (permissions: readonly string[]): boolean =>
  TWO_FACTOR_PERMISSIONS.some((p) => permissions.includes(p));

/** Brand scope. No rows means all brands — "scope: all 3 brands". */
export const canSeeBrand = (principal: Principal, brandId: string): boolean =>
  principal.brandIds.length === 0 || principal.brandIds.includes(brandId);

/**
 * The brands a query should be limited to, or null for "no restriction".
 * Returning null rather than a list of every brand keeps the SQL free of an
 * IN clause that would have to be kept in step with the brand table.
 */
export const brandScopeFilter = (principal: Principal): readonly string[] | null =>
  principal.brandIds.length === 0 ? null : principal.brandIds;

export class AuthorizationError extends Error {
  readonly permission: Permission;
  constructor(permission: Permission) {
    super(`This action requires the ${permission} permission.`);
    this.name = 'AuthorizationError';
    this.permission = permission;
  }
}

export class TwoFactorRequiredError extends Error {
  constructor() {
    super('This action requires a session verified with two-factor authentication.');
    this.name = 'TwoFactorRequiredError';
  }
}

/**
 * Whether a permission that requires a second factor is being exercised by a session that never
 * satisfied one. Holding a two-factor permission is not enough: the SESSION must actually have
 * satisfied the second factor. A trusted-device sign-in that skipped it must not release a
 * withdrawal.
 */
function lacksTheSecondFactor(principal: Principal, permission: Permission): boolean {
  const definition = PERMISSIONS.find((p) => p.key === permission);
  return definition?.requiresTwoFactor === true && !principal.mfa;
}

/**
 * Assert a capability. Throws rather than returning a boolean so a forgotten
 * `if` is impossible — the call either proceeds or it does not.
 */
export function assertCan(principal: Principal, permission: Permission): void {
  if (!can(principal, permission)) throw new AuthorizationError(permission);
  if (lacksTheSecondFactor(principal, permission)) throw new TwoFactorRequiredError();
}

/**
 * `assertCan`'s answer as a boolean: the role holds the permission AND, for one that requires a
 * second factor, the session satisfied it. `can()` is membership alone.
 *
 * For a route that shows or withholds PART of its answer by a permission another route guards with
 * `requirePermission` — the player page sends the name given at sign-up only to a reader the
 * identity case, which shows the same legal name, would let in. Decided by membership, the two
 * agree only while the key requires no second factor.
 */
export function mayExercise(principal: Principal, permission: Permission): boolean {
  return can(principal, permission) && !lacksTheSecondFactor(principal, permission);
}

/**
 * "Super Admin cannot be reduced below access.manage by anyone but another
 * Super Admin." Returns the reason it is refused, or null to allow.
 */
export function refuseRoleChange(
  actor: Principal,
  targetIsSuper: boolean,
  nextPermissions: readonly string[],
): string | null {
  if (!targetIsSuper) return null;
  if (nextPermissions.includes('access.manage')) return null;
  if (actor.isSuper) return null;
  return 'Only another Super Admin may remove access.manage from a Super Admin role.';
}

/** Navigation, grouped exactly as the console groups it. */
export const NAV_GROUPS = [
  {
    label: 'Monitor',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: 'gauge', path: '/', permission: 'players.view' },
      { key: 'risk', label: 'Risk', icon: 'shield-warning', path: '/risk', permission: 'players.restrict' },
      { key: 'reports', label: 'Reports', icon: 'chart-line', path: '/reports', permission: 'audit.export' },
    ],
  },
  {
    label: 'Players',
    items: [
      { key: 'players', label: 'Player management', icon: 'users-three', path: '/players', permission: 'players.view' },
      /* `kyc.review`, NOT `players.view`, and that is the narrower key on purpose.
         This queue opens onto photographs of identity documents; an Auditor holds
         players.view and has no business in it. The same key the routes behind
         it carry, so a role that can see the entry can work the queue — a nav
         item onto a 403 is worse than no nav item. */
      { key: 'identity', label: 'Identity checks', icon: 'identification-card', path: '/identity', permission: 'kyc.review' },
      { key: 'support', label: 'Support', icon: 'lifebuoy', path: '/support', permission: 'players.view' },
    ],
  },
  {
    label: 'Money',
    items: [
      /* READ, not decide. `GET /api/withdrawals` moved to `transactions.view`
         because a payout queue is something a Risk Officer and an Auditor have
         every reason to read and neither can decide; the sidebar followed it,
         so the two agree. The verdict buttons on that screen carry
         `funds.approve` individually — a reader sees the queue and no verdict
         controls, rather than a nav entry that opens onto a 403. */
      { key: 'funds', label: 'Player funds', icon: 'wallet', path: '/funds', permission: 'transactions.view' },
      { key: 'transactions', label: 'Transactions', icon: 'arrows-left-right', path: '/transactions', permission: 'transactions.view' },
      { key: 'payments', label: 'Payment methods', icon: 'credit-card', path: '/payments', permission: 'transactions.view' },
    ],
  },
  {
    label: 'Growth',
    items: [
      /* Reading the promotion catalogue is not publishing one. Gating the
         sidebar on promo.publish hid the module from every role but Super
         Admin, while GET /api/promotions answered all of them — a capability
         they held and could not reach. */
      { key: 'promotions', label: 'Promotions', icon: 'gift', path: '/promotions', permission: 'players.view' },
      { key: 'points', label: 'Points Mall', icon: 'storefront', path: '/points', permission: 'players.view' },
      { key: 'agents', label: 'Agent management', icon: 'share-network', path: '/agents', permission: 'partners.view' },
      { key: 'affiliates', label: 'Affiliates', icon: 'megaphone', path: '/affiliates', permission: 'partners.view' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { key: 'games', label: 'Game management', icon: 'game-controller', path: '/games', permission: 'players.view' },
      /*
       * BRANDS — the operators this platform runs, and their licences.
       *
       * Gated on `platform.view` rather than on `platform.manage`, for the
       * reason this group has now had to state three times: a screen gated on a
       * write capability can only be SHOWN by granting the write. The roles
       * that may read the configuration may open this screen; the create,
       * edit and renew controls on it are the ones that check
       * `platform.manage`, and they say so when the operator does not hold it.
       *
       * It is its own module rather than a panel on /settings because a brand
       * is not a parameter: `platform.brand.domain` is the tenancy boundary the
       * player portal resolves a host against, and the licence expiry is what
       * the dashboard warns on. Both deserve a screen somebody can be sent to.
       */
      { key: 'brands', label: 'Brands & licences', icon: 'buildings', path: '/brands', permission: 'platform.view' },
      /* Reading a parameter is not changing one, and reading the platform's
         health is not administering it. /system is a read-only surface; the
         parameter table on /settings reads for `platform.view` holders and
         WRITES for `platform.manage` holders, which is why the screen is
         reachable by every role that reads the values — the four accountable
         for them, and the PAGCOR Regulator — and editable by one. Everything on
         /maintenance, /announcements and /access keeps
         access.manage and its second factor. */
      { key: 'settings', label: 'Platform settings', icon: 'sliders', path: '/settings', permission: 'platform.view' },
      { key: 'maintenance', label: 'Maintenance', icon: 'wrench', path: '/maintenance', permission: 'access.manage' },
      { key: 'announcements', label: 'Announcements', icon: 'broadcast', path: '/announcements', permission: 'access.manage' },
      { key: 'sysadmin', label: 'System admin', icon: 'hard-drives', path: '/system', permission: 'platform.view' },
      { key: 'access', label: 'Access control', icon: 'lock-key', path: '/access', permission: 'access.manage' },
    ],
  },
] as const satisfies readonly {
  label: string;
  items: readonly { key: string; label: string; icon: string; path: string; permission: Permission }[];
}[];

/**
 * The navigation this principal may see.
 * "Permission-gated actions are HIDDEN, not disabled" — a control the user
 * cannot use should not advertise a capability they do not have.
 */
export function visibleNav(principal: Principal) {
  return NAV_GROUPS.map((g) => ({
    label: g.label,
    items: g.items.filter((i) => can(principal, i.permission)),
  })).filter((g) => g.items.length > 0);
}

/**
 * Where the console's home link — the brand mark — takes this principal: the first module their
 * sidebar offers. The dashboard for every role holding `players.view`; Reports for the PAGCOR
 * Regulator, which holds no player key and for which `/` would only be a refusal. `/` when the
 * sidebar offers nothing at all.
 */
export function homePath(principal: Principal): string {
  return visibleNav(principal)[0]?.items[0]?.path ?? '/';
}
